import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { contactAPI } from '@/services/api';
import { Bell, Mail, User, Calendar, RefreshCw, Loader2, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const AdmNotifications = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const data = await contactAPI.getAllMessages();
      console.log('API Response:', data);
      const contactsArray = data.data || data.messages || [];
      setContacts(contactsArray);
      toast.success('Messages chargés avec succès');
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (id) => {
    try {
      await contactAPI.deleteMessage(id);
      toast.success('Message supprimé avec succès');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Erreur lors de la suppression du message');
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non disponible';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Bell className="w-6 h-6 mr-2" />
          Notifications & Messages
        </h2>
        <Button onClick={fetchContacts} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Chargement...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </>
          )}
        </Button>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-gray-600 dark:text-gray-400">
          {contacts.length} message(s) reçu(s)
        </p>
      </div>

      {loading && contacts.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : contacts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-600 dark:text-gray-400">
            <Bell className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Aucun message reçu pour le moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#374151 #1f2937', scrollbarGutter: 'stable' }}>
          {contacts.map((contact) => (
            <Card
              key={contact.id}
              className={`border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e] transition-all ${
                selectedContact === contact.id ? 'ring-2 ring-emerald-500' : ''
              }`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg text-gray-900 dark:text-white flex items-center gap-2">
                      <User className="w-5 h-5 text-emerald-500" />
                      {contact.name}
                    </CardTitle>
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        <a
                          href={`mailto:${contact.email}`}
                          className="hover:text-emerald-500"
                        >
                          {contact.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(contact.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedContact(selectedContact === contact.id ? null : contact.id)}
                  >
                    {selectedContact === contact.id ? 'Masquer' : 'Voir le message'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMessage(contact.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                  </div>
                </div>
              </CardHeader>
              {selectedContact === contact.id && (
                <CardContent className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-[#1f2937] dark:bg-[#242b3d]">
                    <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Message
                    </h4>
                    <p className="whitespace-pre-wrap text-gray-600 dark:text-gray-400">
                      {contact.message}
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = `mailto:${contact.email}`}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Répondre par email
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdmNotifications;
